/**
 * SPRINT 3 (S2-1): 플립북 페이지↔스프레드 매핑 단일 정본.
 *
 * G3 "스프레드 착지 오차"의 원인은 계산이 틀려서가 아니라, **같은 계산이 여러
 * 곳에 서로 다른 형태로 흩어져 있어서**였다. react-pageflip은 `showCover=true`
 * 가로 모드에서 페이지를 이렇게 짝짓는다(0-based 인덱스 기준):
 *
 *     인덱스 0        → 표지 1쪽 (혼자, 오른쪽)
 *     인덱스 1,2      → 2·3쪽 스프레드
 *     인덱스 3,4      → 4·5쪽 스프레드
 *     인덱스 2k-1,2k  → 2k·(2k+1)쪽 스프레드
 *
 * 그래서 `turnToPage(9 - 1)` = `turnToPage(8)`을 부르면 화면엔 8·9쪽이 뜨지만
 * react-pageflip의 `onFlip`은 그 스프레드의 **왼쪽** 인덱스(7)를 돌려준다 —
 * 카운터는 8을 표시하고, URL 해시는 9, 썸네일은 9만 강조되는 식으로 셋이
 * 제각각 어긋났다.
 *
 * 해결: "지금 어디를 보고 있나"의 정본을 **스프레드에서 처음 보이는 쪽 번호**
 * 하나로 못박고, 카운터·슬라이더·썸네일·URL 해시·turnToPage가 전부 이 파일의
 * 함수만 거치게 한다. 세로(포트레이트) 단면 모드는 짝짓기가 없어 페이지 =
 * 스프레드지만, 분기를 호출부마다 두지 않도록 여기서 같이 흡수한다.
 */

/** 한 스프레드에 실제로 보이는 쪽 번호들(1-based). 항상 1개 또는 2개. */
export function visiblePages(page: number, totalPages: number, isPortrait: boolean): number[] {
  if (totalPages <= 0) return [];
  const p = clampPage(page, totalPages);
  if (isPortrait) return [p];
  if (p <= 1) return [1];               // 표지는 항상 혼자
  const left = p % 2 === 0 ? p : p - 1; // 2,3 → 2 / 4,5 → 4 …
  const right = left + 1;
  return right <= totalPages ? [left, right] : [left];
}

/**
 * 정본 페이지 번호 — 어떤 쪽을 요청하든 그 쪽이 실제로 보이는 스프레드의
 * **처음 보이는 쪽**으로 정규화한다. 9쪽을 요청하면 8·9가 보이므로 8을 돌려준다.
 * 카운터·슬라이더·URL 해시가 전부 이 값을 쓰기 때문에 셋이 어긋날 수가 없다.
 */
export function anchorPage(page: number, totalPages: number, isPortrait: boolean): number {
  const pages = visiblePages(page, totalPages, isPortrait);
  return pages.length ? pages[0] : 1;
}

/**
 * react-pageflip `turnToPage()`에 넘길 0-based 인덱스.
 * 정규화된 정본 페이지를 기준으로 계산하므로 "요청한 쪽과 착지한 스프레드가
 * 다른" 경우가 생기지 않는다.
 */
export function pageToFlipIndex(page: number, totalPages: number, isPortrait: boolean): number {
  return anchorPage(page, totalPages, isPortrait) - 1;
}

/**
 * react-pageflip `onFlip`의 `e.data`(착지한 스프레드의 왼쪽 인덱스)를 정본
 * 페이지 번호로 되돌린다. 이 변환을 거치지 않고 `e.data + 1`을 그대로 쓰면
 * 표지 이후 모든 스프레드에서 1쪽씩 밀린다 — G3의 직접 원인이었다.
 */
export function flipIndexToPage(flipIndex: number, totalPages: number, isPortrait: boolean): number {
  return anchorPage(flipIndex + 1, totalPages, isPortrait);
}

/** 카운터·툴팁에 쓸 표시 라벨. 스프레드면 "8-9", 단면이면 "8". */
export function pageLabel(page: number, totalPages: number, isPortrait: boolean): string {
  const pages = visiblePages(page, totalPages, isPortrait);
  if (pages.length === 0) return '0';
  return pages.length === 2 ? `${pages[0]}-${pages[1]}` : `${pages[0]}`;
}

/** 썸네일 강조용 — 그 쪽이 지금 화면에 보이는가(스프레드면 두 쪽 다 true). */
export function isPageVisible(
  pageNumber: number,
  currentPage: number,
  totalPages: number,
  isPortrait: boolean
): boolean {
  return visiblePages(currentPage, totalPages, isPortrait).includes(pageNumber);
}

/** 마지막 스프레드에 이미 있는가(다음 버튼 비활성화 판정용). */
export function isLastSpread(page: number, totalPages: number, isPortrait: boolean): boolean {
  const pages = visiblePages(page, totalPages, isPortrait);
  return pages.length > 0 && pages[pages.length - 1] >= totalPages;
}

export function isFirstSpread(page: number, totalPages: number, isPortrait: boolean): boolean {
  return anchorPage(page, totalPages, isPortrait) <= 1;
}

function clampPage(page: number, totalPages: number): number {
  if (!Number.isFinite(page)) return 1;
  return Math.min(Math.max(Math.round(page), 1), Math.max(1, totalPages));
}
